import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getPublicProjets } from '../services/siteContentService';
import { usePublicPaths } from '../routes/publicPaths';
import ResponsiveImage from './ResponsiveImage';
import { prefetchProjetCardImages, PROJET_CARD_SIZES } from '../utils/prefetchProgramHero';

const FEATURED_CACHE_KEY = 'khayr_featured_projets_v3';

const readFeaturedCache = () => {
  try {
    const raw = localStorage.getItem(FEATURED_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) && parsed.length > 0 ? parsed : null;
  } catch {
    return null;
  }
};

const writeFeaturedCache = (projets) => {
  try {
    if (!projets.length) {
      localStorage.removeItem(FEATURED_CACHE_KEY);
      return;
    }
    localStorage.setItem(FEATURED_CACHE_KEY, JSON.stringify(projets));
  } catch {
    // ignore quota errors
  }
};

const FeaturedProjectsSection = () => {
  const { t } = useTranslation();
  const paths = usePublicPaths();
  const [projets, setProjets] = useState(() => readFeaturedCache() || []);

  useEffect(() => {
    getPublicProjets({ featured: true })
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setProjets(list);
        writeFeaturedCache(list);
        if (list.length > 0) {
          prefetchProjetCardImages(list, 3);
        }
      })
      .catch(() => {});
  }, []);

  if (projets.length === 0) return null;

  return (
    <section className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-2">{t('public.home.featuredTitle')}</h2>
        <div className="w-24 h-1 bg-brand-divider mx-auto mb-10" />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {projets.map((projet, index) => (
            <div key={projet.id} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow">
              {projet.image_url ? (
                <ResponsiveImage
                  src={projet.image_url}
                  webpSm={projet.image_url_webp_sm}
                  webpCard={projet.image_url_webp_card}
                  alt={projet.title}
                  className="w-full h-44 object-cover"
                  sizes={PROJET_CARD_SIZES}
                  loading={index < 3 ? 'eager' : 'lazy'}
                  fetchPriority={index < 3 ? 'low' : 'auto'}
                  decoding="async"
                />
              ) : (
                <div className="w-full h-44 bg-gradient-to-br from-brand-primary via-brand-blue to-brand-secondary flex items-center justify-center text-white text-4xl">
                  💧
                </div>
              )}
              <div className="p-5">
                {(projet.category_label || projet.category) && (
                  <span className="text-xs font-medium text-brand-primary bg-brand-surface px-2 py-1 rounded">
                    {projet.category_label || projet.category}
                  </span>
                )}
                <h3 className="text-lg font-bold text-gray-900 mt-2 mb-1">{projet.title}</h3>
                {projet.region && <p className="text-sm text-gray-500 mb-3">{projet.region}</p>}
                <div className="mb-2">
                  <div className="flex justify-between text-sm text-gray-600 mb-1">
                    <span>{projet.progress_percent}%</span>
                    <span>{parseFloat(projet.collected_amount).toLocaleString()} / {parseFloat(projet.goal_amount).toLocaleString()} {t('public.common.currency')}</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-brand-primary h-2 rounded-full transition-all"
                      style={{ width: `${projet.progress_percent}%` }}
                    />
                  </div>
                </div>
                <Link
                  to={paths.projet(projet.slug)}
                  className="inline-block mt-3 text-brand-primary font-medium hover:underline"
                >
                  {t('public.home.moreDetails')}
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturedProjectsSection;
